import { CONTACT_TYPES, ORGANIZATION_TYPES, VENDORS_SET_ASIDES } from './constants';

export const formatPhoneInput = (value) => {
  const digits = String(value || '').replaceAll(/\D/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1') ? digits.substring(1) : digits;
  const limitedDigits = normalizedDigits.substring(0, 10);

  if (limitedDigits.length <= 3) {
    return limitedDigits;
  }

  if (limitedDigits.length <= 6) {
    return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
  }

  return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
};

export const isValidEmail = (value) => {
  if (!value) {
    return false;
  }

  const trimmed = String(value).trim();
  const atIndex = trimmed.indexOf('@');

  if (atIndex <= 0 || atIndex !== trimmed.lastIndexOf('@') || trimmed.includes(' ')) {
    return false;
  }

  const domain = trimmed.slice(atIndex + 1);
  const dotIndex = domain.indexOf('.');

  return dotIndex > 0 && dotIndex < domain.length - 1;
};

export const isValidCompanyPhone = (value) => {
  if (!value) {
    return true;
  }

  const digits = String(value).replaceAll(/\D/g, '');
  const normalized = digits.length === 11 && digits.startsWith('1') ? digits.substring(1) : digits;

  return normalized.length === 10;
};

export const trimValue = (value) => String(value || '').trim();

export const emptyToNull = (value) => {
  const trimmedValue = trimValue(value);
  return trimmedValue || null;
};

export const getSelectedSetAsideObjects = (selectedCodes = []) => {
  return selectedCodes
    .map((code) => {
      const match = VENDORS_SET_ASIDES.find((item) => item.code === code);

      if (!match) {
        return null;
      }

      return {
        code: match.code,
        description: match.name,
      };
    })
    .filter(Boolean);
};

export const getSetAsideRenderValue = (selectedCodes = []) => {
  if (!selectedCodes.length) {
    return 'Select...';
  }

  return selectedCodes
    .map((code) => {
      const match = VENDORS_SET_ASIDES.find((item) => item.code === code);
      return match?.name || code;
    })
    .join(', ');
};

export const getCompanyName = (company = {}) => {
  return company.name || company.companyName || company.organizationName || '-';
};

export const getCompanyMeta = (company = {}) => {
  const values = [];

  if (company.ueiCode) {
    values.push(`UEI: ${company.ueiCode}`);
  }

  if (company.cageCode) {
    values.push(`Cage Code: ${company.cageCode}`);
  }

  if (company.companySize) {
    values.push(`Size: ${company.companySize}`);
  }

  return values.join('   ');
};

export const getVendorName = (vendor = {}) => {
  return vendor.name || vendor.companyName || vendor.organizationName || '-';
};

export const getVendorMeta = (vendor = {}) => {
  const values = [];

  if (vendor.ueiCode) {
    values.push(`UEI: ${vendor.ueiCode}`);
  }

  if (vendor.cageCode) {
    values.push(`Cage Code: ${vendor.cageCode}`);
  }

  if (vendor.primaryNaics) {
    values.push(`NAICS: ${vendor.primaryNaics}`);
  }

  if (vendor.city || vendor.state) {
    values.push([vendor.city, vendor.state].filter(Boolean).join(', '));
  }

  return values.join('   ');
};

export const getFederalAgencyName = (agency = {}) => {
  return agency.name || '-';
};

export const getFederalAgencyMeta = (agency = {}) => {
  const values = [];

  if (agency.type) {
    values.push(agency.type);
  }

  if (agency.city || agency.state) {
    values.push([agency.city, agency.state].filter(Boolean).join(', '));
  }

  return values.join('   ');
};

export const validateContactDetails = (values) => {
  const errors = {};

  if (!trimValue(values.firstName)) {
    errors.firstName = 'First Name is required';
  }

  if (!trimValue(values.lastName)) {
    errors.lastName = 'Last Name is required';
  }

  if (!trimValue(values.contactType)) {
    errors.contactType = 'Type is required';
  }

  if (!trimValue(values.assignedTo)) {
    errors.assignedTo = 'Assigned To is required';
  }

  if (!trimValue(values.origin)) {
    errors.origin = 'Origin is required';
  }

  if (values.origin === 'other' && !trimValue(values.originDetail)) {
    errors.originDetail = 'Please specify origin details';
  }

  if (!trimValue(values.email)) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(trimValue(values.email))) {
    errors.email = 'Please enter a valid email address';
  }

  if (!trimValue(values.primaryPhone)) {
    errors.primaryPhone = 'Phone is required';
  }

  if (trimValue(values.secondaryEmail) && !isValidEmail(trimValue(values.secondaryEmail))) {
    errors.secondaryEmail = 'Please enter a valid email address';
  }

  return errors;
};

export const validateOrganizationDetails = (
  values,
  selectedCrmCompany,
  selectedRegisteredVendor,
  selectedFederalAgency
) => {
  const errors = {};

  if (values.organizationType === ORGANIZATION_TYPES.NEW && !trimValue(values.organizationName)) {
    errors.organizationName = 'Organization is required';
  }

  if (values.organizationType === ORGANIZATION_TYPES.CRM_COMPANY && !selectedCrmCompany?.id) {
    errors.crmCompany = values.contactType === 'agent' ? 'Please select a CRM agency' : 'Please select an organization';
  }

  if (values.organizationType === ORGANIZATION_TYPES.REGISTERED_VENDOR && !selectedRegisteredVendor?.id) {
    errors.registeredVendor = 'Please select a registered vendor';
  }

  if (values.organizationType === ORGANIZATION_TYPES.GOVERNMENT_AGENCY && !selectedFederalAgency?.id) {
    errors.federalAgency = 'Please select a federal agency';
  }

  if (trimValue(values.companyEmail) && !isValidEmail(trimValue(values.companyEmail))) {
    errors.companyEmail = 'Please enter a valid email address';
  }

  if (trimValue(values.companyPhone) && !isValidCompanyPhone(trimValue(values.companyPhone))) {
    errors.companyPhone = 'Please enter a valid 10-digit phone number';
  }

  return errors;
};

export const getLockedOrganizationType = (organization, prefillsContactType) => {
  if (organization?.id) {
    return ORGANIZATION_TYPES.CRM_COMPANY;
  }

  if (prefillsContactType === CONTACT_TYPES.AGENT) {
    return ORGANIZATION_TYPES.GOVERNMENT_AGENCY;
  }

  return ORGANIZATION_TYPES.NEW;
};

export const getVisibleFormErrors = ({
  errors,
  contactDetailsErrors,
  touched,
  submitAttempted,
  contactDetailsAttempted,
}) => {
  const contactErrorKeys = Object.keys(contactDetailsErrors);

  return Object.keys(errors).reduce((result, key) => {
    const isContactError = contactErrorKeys.includes(key);
    const shouldShowContactError = isContactError && (touched[key] || submitAttempted || contactDetailsAttempted);
    const shouldShowOrganizationError = !isContactError && (touched[key] || submitAttempted);

    if (shouldShowContactError || shouldShowOrganizationError) {
      result[key] = errors[key];
    }

    return result;
  }, {});
};

export const hasAssignedOrganization = ({
  isOrganizationLocked,
  values,
  selectedCrmCompany,
  selectedRegisteredVendor,
  selectedFederalAgency,
}) => {
  if (isOrganizationLocked) {
    return true;
  }

  if (values.organizationType === ORGANIZATION_TYPES.NEW) {
    return Boolean(trimValue(values.organizationName));
  }

  if (values.organizationType === ORGANIZATION_TYPES.CRM_COMPANY) {
    return Boolean(selectedCrmCompany?.id);
  }

  if (values.organizationType === ORGANIZATION_TYPES.REGISTERED_VENDOR) {
    return Boolean(selectedRegisteredVendor?.id);
  }

  if (values.organizationType === ORGANIZATION_TYPES.GOVERNMENT_AGENCY) {
    return Boolean(selectedFederalAgency?.id);
  }

  return false;
};

const appendLockedOrganizationAssignment = (payload, lockedOrganization, values) => {
  if (lockedOrganization?.id) {
    payload.crmCompanyId = lockedOrganization.id;
    payload.organizationAssignment = {
      type: ORGANIZATION_TYPES.CRM_COMPANY,
      id: lockedOrganization.id,
      name: getCompanyName(lockedOrganization),
    };
    return;
  }

  if (lockedOrganization?.organizationAssignment?.type) {
    payload.organizationAssignment = lockedOrganization.organizationAssignment;
    return;
  }

  payload.organizationAssignment = {
    type: values.contactType === CONTACT_TYPES.AGENT ? ORGANIZATION_TYPES.GOVERNMENT_AGENCY : ORGANIZATION_TYPES.NEW,
    name: trimValue(lockedOrganization?.name),
    details: lockedOrganization?.details || null,
  };
};

const appendSelectedOrganizationAssignment = (
  payload,
  values,
  selectedCrmCompany,
  selectedRegisteredVendor,
  selectedFederalAgency
) => {
  if (values.organizationType === ORGANIZATION_TYPES.CRM_COMPANY) {
    payload.crmCompanyId = selectedCrmCompany.id;
    payload.organizationAssignment = {
      type: ORGANIZATION_TYPES.CRM_COMPANY,
      id: selectedCrmCompany.id,
      name: getCompanyName(selectedCrmCompany),
    };
    return;
  }

  if (values.organizationType === ORGANIZATION_TYPES.REGISTERED_VENDOR) {
    payload.organizationAssignment = {
      type: ORGANIZATION_TYPES.REGISTERED_VENDOR,
      vendorId: selectedRegisteredVendor.vendorId || selectedRegisteredVendor.ueiCode || selectedRegisteredVendor.id,
      name: getVendorName(selectedRegisteredVendor),
      details: selectedRegisteredVendor,
    };
    return;
  }

  if (values.organizationType === ORGANIZATION_TYPES.GOVERNMENT_AGENCY) {
    payload.organizationAssignment = {
      type: ORGANIZATION_TYPES.GOVERNMENT_AGENCY,
      id: selectedFederalAgency.id,
      name: getFederalAgencyName(selectedFederalAgency),
      details: selectedFederalAgency,
    };
    return;
  }

  const selectedSetAsides = getSelectedSetAsideObjects(values.setAsides);
  payload.companyName = trimValue(values.organizationName);
  payload.organizationAssignment = {
    type: ORGANIZATION_TYPES.NEW,
    name: trimValue(values.organizationName),
    ueiCode: emptyToNull(values.ueiCode),
    cageCode: emptyToNull(values.cageCode),
    setAsides: selectedSetAsides,
    companySize: emptyToNull(values.companySize),
    naics: emptyToNull(values.naics),
    companyEmail: emptyToNull(values.companyEmail),
    companyPhone: emptyToNull(values.companyPhone),
  };
};

export const buildContactCreatePayload = ({
  values,
  isOrganizationLocked,
  lockedOrganization,
  selectedCrmCompany,
  selectedRegisteredVendor,
  selectedFederalAgency,
}) => {
  const payload = {
    firstName: trimValue(values.firstName),
    lastName: trimValue(values.lastName),
    title: emptyToNull(values.title),
    contactType: emptyToNull(values.contactType),
    assignedTo: values.assignedTo,
    origin: emptyToNull(values.origin),
    originDetail: values.origin === 'other' ? emptyToNull(values.originDetail) : null,
    email: emptyToNull(values.email),
    primaryPhone: emptyToNull(values.primaryPhone),
    secondaryEmail: emptyToNull(values.secondaryEmail),
    secondaryPhone: emptyToNull(values.secondaryPhone),
    comments: emptyToNull(values.comments),
    organizationNotes: emptyToNull(values.organizationNotes),
    source: 'manual',
    stage: 'New',
    disposition: 'New',
  };

  if (isOrganizationLocked) {
    appendLockedOrganizationAssignment(payload, lockedOrganization, values);
  } else {
    appendSelectedOrganizationAssignment(
      payload,
      values,
      selectedCrmCompany,
      selectedRegisteredVendor,
      selectedFederalAgency
    );
  }

  return payload;
};

export const getSubmitErrorTab = (values) => {
  if (Object.keys(validateContactDetails(values)).length) {
    return 'contact';
  }

  return 'organization';
};
